import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UsuariosSubTab, OusSubTab, DominiosSubTab, CentinelasSubTab, ANombreDeSubTab, type JobsSubTab } from './subTabs';
import './JobsTab.css';

/**
 * 🔴 Sub-pestañas con la MISMA primitiva que el resto del panel (`.tpl-subnav`, la de Plantillas),
 * no una barra propia. La pantalla venía apilando en una sola columna la configuración de usuarios,
 * la de unidades organizacionales, el catálogo de dominios y las cinco corridas: todo amontonado, y
 * con su propio diseño encima.
 *
 * Cada pestaña lleva su configuración arriba y su histórico de corridas debajo, que es el orden en
 * que se lee: la fuente decide cuáles de esas corridas van a hacer algo de verdad, así que leerla
 * después sería leerla al revés.
 */
const SUB_TABS: Array<{ id: JobsSubTab; icon: string; labelKey: string }> = [
  { id: 'usuarios',  icon: 'fa-users',   labelKey: 'jobs.tabs.users' },
  { id: 'ous',       icon: 'fa-sitemap', labelKey: 'jobs.tabs.ous' },
  { id: 'dominios',  icon: 'fa-at',      labelKey: 'jobs.tabs.domains' },
  { id: 'centinelas', icon: 'fa-envelope-open-text', labelKey: 'jobs.tabs.sentinels' },
  { id: 'aNombreDe', icon: 'fa-user-check', labelKey: 'jobs.tabs.delegation' },
];

export function JobsTab() {
  const { t } = useTranslation();
  const [sub, setSub] = useState<JobsSubTab>('usuarios');
  const barra = useRef<HTMLDivElement>(null);

  /**
   * 🔴 Flechas para moverse entre pestañas.
   *
   * No es un extra: `role="tablist"` **promete** este comportamiento (patrón ARIA de pestañas), y una
   * lista que se anuncia como tal pero sólo responde al ratón es peor que no anunciarla — quien
   * navega con teclado pulsa la flecha, no pasa nada, y no hay forma de saber que había que tabular.
   * Con `aria-controls` + `role="tabpanel"` el panel queda asociado a su pestaña, que es la otra
   * mitad del patrón.
   */
  const alPulsarTecla = (e: React.KeyboardEvent) => {
    const paso = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (paso === 0) return;
    e.preventDefault();

    const actual = SUB_TABS.findIndex(x => x.id === sub);
    const siguiente = SUB_TABS[(actual + paso + SUB_TABS.length) % SUB_TABS.length];
    setSub(siguiente.id);
    // El foco sigue a la selección, como exige el patrón cuando la pestaña se activa al moverse.
    barra.current?.querySelector<HTMLButtonElement>(`#jobs-tab-${siguiente.id}`)?.focus();
  };

  return (
    <div className="adm-tab-content jobs-tab">
      <header className="page-header">
        <div>
          <h2 className="page-title">
            <i className="fas fa-sync" aria-hidden="true" /> {t('jobs.title')}
          </h2>
          <p className="page-subtitle">{t('jobs.subtitle')}</p>
        </div>
      </header>

      <div className="tpl-subnav" role="tablist" ref={barra} onKeyDown={alPulsarTecla}>
        {SUB_TABS.map(subTab => (
          <button
            key={subTab.id}
            id={`jobs-tab-${subTab.id}`}
            type="button"
            role="tab"
            aria-selected={sub === subTab.id}
            aria-controls={`jobs-panel-${subTab.id}`}
            // Sólo la pestaña activa entra en el orden de tabulación: dentro del tablist se navega
            // con flechas. Es la otra mitad del patrón, y sin ella se tabula por las tres.
            tabIndex={sub === subTab.id ? 0 : -1}
            className={`tpl-subnav-btn${sub === subTab.id ? ' tpl-subnav-active' : ''}`}
            onClick={() => setSub(subTab.id)}
          >
            <i className={`fas ${subTab.icon}`} aria-hidden="true" /> {t(subTab.labelKey)}
          </button>
        ))}
      </div>

      <div
        id={`jobs-panel-${sub}`}
        role="tabpanel"
        aria-labelledby={`jobs-tab-${sub}`}
        className="jobs-panel"
      >
        {sub === 'usuarios' && <UsuariosSubTab />}
        {sub === 'ous'      && <OusSubTab />}
        {sub === 'dominios' && <DominiosSubTab />}
        {sub === 'centinelas' && <CentinelasSubTab />}
        {sub === 'aNombreDe' && <ANombreDeSubTab />}
      </div>
    </div>
  );
}
